/**
 * Browser adapter for LexisNexis Geofence Verify, RADAR_DIRECT delivery.
 *
 * REWRITTEN 2026-09-17 against the vendor's own sample integration, which
 * differs from their written documentation in ways that matter:
 *
 *   - There is no `threatmetrix.Geofence` namespace to call. Under RADAR_DIRECT
 *     the "wrapper" is roughly thirty lines of glue around the Radar SDK, and
 *     WE own it. That is why `window.threatmetrix.Geofence` was undefined in our
 *     shipped bundle: it was never going to exist, and the "advanced profiling
 *     tag" we were chasing was never the missing piece.
 *   - `init` takes a config object, not `(apiKey, callback)`.
 *   - The one-shot check returns a PROMISE. The doc described only the callback.
 *   - `skipVerifyApp` is passed by us, per call, not configured on their side.
 *
 * Owning the wrapper is a real advantage rather than extra work: it is what
 * lets us bind a check to a player (see `Radar.setUserId` below), which the
 * documented API gave us no way to do.
 *
 * Scope stays one check, at login. We do not call `startTrackingVerified`.
 *
 * WHERE `window.Radar` COMES FROM, verified live 2026-09-17 against
 * app.hijackpoker-test6.com: we do NOT load the Radar SDK ourselves, and we do
 * NOT need a different ThreatMetrix client library. The tag we already ship
 * (`device-profiler.js`) fetches its real payload from `h.online-metrix.net`
 * at runtime, and now that geofencing is enabled on the org account that
 * payload publishes `window.Radar` (v4.5.6, carrying `initialize`,
 * `setUserId`, `trackVerified`, `onTokenUpdated`, `onError` and the rest).
 *
 * The consequence is a sequencing dependency that is easy to miss and fatal if
 * missed: Radar appears roughly a second AFTER `threatmetrix.profile()` runs,
 * and never appears at all if it does not. Waiting for the global without
 * triggering profiling first would report `sdk_unavailable` forever. That is
 * why this module starts profiling itself rather than assuming someone else
 * already did.
 *
 * It also means the org id is load-bearing for geofencing, not just for risk
 * scoring: `profile()` silently does nothing unless the org id is exactly 8
 * characters, so an environment with an empty `REACT_APP_THREATMATRIX_ORG_ID`
 * can never load Radar.
 *
 * Reference: vendor sample integration, 2026-09-17.
 */

/**
 * How long we wait for a verdict before giving up on the vendor.
 *
 * MEASURED, do not lower without re-measuring. On a Galaxy S24+ (Android 16,
 * Chrome WebView 152) inside this app's own WebView, a coarse fix with
 * `maximumAge: 0` took 14.0s on the permission-prompt run and then 10.4s and
 * 29.9s on two warm runs. The native Capacitor plugin was no better: 6.7s and
 * 29.9s. This is a property of cold location fixes on real hardware, not of the
 * WebView bridge or of the vendor.
 *
 * 45s allows the worst observed fix plus the vendor's network round trip.
 * Radar applies its own internal timeout that we cannot see; if theirs is
 * shorter, ours never gets a chance to matter, and their error arrives first.
 */
import { getNewSessionId, TMXPageData } from './threatmetrix';

const SOFT_CHECK_TIMEOUT_MS = 45_000;

/**
 * Error surfaced when the Radar Verify desktop app is not running.
 *
 * Should never occur: we pass `skipVerifyApp: true`. Kept and classified
 * separately precisely for that reason. It is not a metric now, it is an ALARM
 * meaning the flag stopped being sent and desktop players are being asked to
 * install software we never asked them to install.
 */
export const ERROR_VERIFY_APP = 'ERROR_VERIFY_APP';

export type GeofenceOutcome =
  /** Vendor returned a verdict. `passed` is theirs; the server still decides. */
  | { status: 'verdict'; passed: boolean; token: string | null; failureReasons: string[] }
  /** We could not get a verdict. `code` says why; never treat this as a pass. */
  | { status: 'unavailable'; code: string };

/**
 * The verified-location response. `token` is the signed JWT our server checks;
 * every other field here is advisory, because the page can edit all of it.
 */
type VerifiedResponse = {
  passed?: boolean;
  token?: string;
  expiresAt?: string;
  failureReasons?: string[];
  user?: {
    updatedAt?: string;
    state?: { name?: string; code?: string };
    country?: { name?: string; code?: string };
  };
};

/** The subset of the Radar web SDK this adapter uses. */
export type RadarSdk = {
  initialize: (apiKey: string) => void;
  setUserId: (userId: string | null) => void;
  setMetadata?: (metadata: Record<string, string>) => void;
  onTokenUpdated: (cb: (token: VerifiedResponse) => void) => void;
  onError: (cb: (err: { status?: string }) => void) => void;
  trackVerified: (opts: { skipVerifyApp?: boolean }) => Promise<VerifiedResponse>;
  startTrackingVerified?: (opts: Record<string, unknown>) => void;
  stopTrackingVerified?: () => void;
};

declare global {
  interface Window {
    Radar?: RadarSdk;
  }
}

/**
 * Stages where a synthetic verdict IS honoured. An allowlist, not a denylist.
 *
 * This was previously `LIVE_STAGES = ['production', 'live']` with the mock
 * accepted on every stage that did not match. Under that shape a stage named
 * anything unexpected -- a typo, a newly introduced production-like stage, a
 * rename -- silently became mockable, so a deployment naming error could turn a
 * real geofence check into a client-controlled development path. The failure
 * was invisible: nothing errors, the mock just starts being honoured.
 *
 * Inverted, an unrecognised stage gets no mock. The cost of being wrong is a
 * developer having to add their stage here; the cost of the old shape was a
 * production geofence that an environment variable could switch off.
 */
const MOCKABLE_STAGES = ['local', 'development', 'develop', 'develop2', 'develop3'];

/**
 * Synthetic outcome for local and pre-credential work.
 *
 * GUARDED ON STAGE, NOT NODE_ENV. `react-scripts build` sets
 * `NODE_ENV=production` for every build it produces, lower environments
 * included, so a NODE_ENV guard means "anything ever built" and would make this
 * dead everywhere except `npm start`. `REACT_APP_ENVIRONMENT` carries the real
 * stage. The safety property is unchanged: a geofence check that an environment
 * variable can switch off is not a geofence check, so only explicitly listed
 * development stages honour it.
 */
function mockOutcome(): GeofenceOutcome | null {
  const mock = process.env.REACT_APP_GEOFENCE_MOCK;
  const stage = (process.env.REACT_APP_ENVIRONMENT ?? '').toLowerCase();
  if (!mock || !MOCKABLE_STAGES.includes(stage)) return null;

  switch (mock) {
    case 'pass':
      return { status: 'verdict', passed: true, token: 'mock.geofence.token', failureReasons: [] };
    case 'fail':
      return {
        status: 'verdict',
        passed: false,
        token: 'mock.geofence.token',
        failureReasons: ['mocked_failure']
      };
    case 'verify-app':
      return { status: 'unavailable', code: ERROR_VERIFY_APP };
    default:
      return { status: 'unavailable', code: 'mock_unavailable' };
  }
}

/**
 * Obvious placeholders that mean "nobody has configured this yet".
 *
 * GitHub will not save an empty Actions secret, so an environment waiting on a
 * real credential ends up holding something like `test`. Without this, that
 * value is truthy, sails past the guard, and the failure resurfaces later as a
 * vendor error that reads like the SDK is broken rather than a key never set.
 *
 * An explicit short list rather than a format check: wrongly rejecting a valid
 * credential would be far worse than accepting a silly one.
 */
const PLACEHOLDER_API_KEYS = ['test', 'todo', 'changeme', 'placeholder', 'none', 'xxx'];

function isUsableApiKey(apiKey: string | undefined): apiKey is string {
  if (!apiKey) return false;
  return !PLACEHOLDER_API_KEYS.includes(apiKey.trim().toLowerCase());
}

/**
 * How long to wait for the Radar global to appear after page load.
 *
 * The client library publishes `window.Radar` asynchronously. The vendor's own
 * sample copes with this by wrapping its entire call sequence in a hardcoded
 * `setTimeout(..., 3000)` before touching the SDK, which is a strong hint that
 * the global is not there the moment the script tag finishes.
 *
 * A fixed sleep is the wrong shape for production: it penalises every fast load
 * and still fails a slow one. Polling to a deadline is fast when the SDK is
 * ready and patient when it is not. 8s is generous against their 3s guess
 * because a cold mobile connection is nothing like a developer's laptop, and
 * the cost of waiting is far lower than the cost of reporting a spurious
 * `sdk_unavailable` for a player whose SDK was merely late.
 */
const RADAR_LOAD_TIMEOUT_MS = 8_000;
const RADAR_POLL_INTERVAL_MS = 100;
const RADAR_POLL_ATTEMPTS = RADAR_LOAD_TIMEOUT_MS / RADAR_POLL_INTERVAL_MS;

/**
 * Resolve once `window.Radar` exists, or null if it never shows up.
 *
 * Bounded by a fixed number of polls rather than a wall-clock deadline. Both
 * express the same budget, but counting ticks is honest about what actually
 * governs the loop and does not depend on `Date.now()` advancing in step with
 * the timers, which it does not under test.
 */
async function waitForRadar(): Promise<RadarSdk | null> {
  if (window.Radar) return window.Radar;

  for (let attempt = 0; attempt < RADAR_POLL_ATTEMPTS; attempt += 1) {
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, RADAR_POLL_INTERVAL_MS));
    if (window.Radar) return window.Radar;
  }
  return null;
}

function toOutcome(response: VerifiedResponse | undefined): GeofenceOutcome {
  const reasons = response?.failureReasons;
  return {
    status: 'verdict',
    passed: response?.passed === true,
    token: response?.token ?? null,
    failureReasons: Array.isArray(reasons) ? reasons : []
  };
}

/**
 * Run the one-shot login geofence check.
 *
 * Never throws and never reports a pass it did not receive. Every failure path
 * resolves to `unavailable`, and it is the SERVER's job to decide what that
 * means. Deciding it here would put the rule in the one place a player can edit.
 *
 * STALENESS: this fires once, at login, and the token carries a ~20 minute
 * expiry, so a long session runs on an old verdict. That is the accepted
 * trade-off for login-only checking; `startTrackingVerified` is the vendor's
 * answer if compliance later requires continuous verification.
 *
 * @param playerGUID Bound to the check via `Radar.setUserId`, so the verdict is
 *   tied to an account and a token minted for one player cannot be presented by
 *   another. The server enforces the match. Note the vendor's own sample passes
 *   `setUserId(null)` for a one-shot check and only sets a real id when polling;
 *   doing it here is a deliberate departure, and it is the whole reason owning
 *   the wrapper is worth something.
 */
/**
 * The check currently in flight, if any, keyed by the player it is for.
 *
 * THE VENDOR CHECK IS SINGLE-FLIGHT BY NATURE and was not enforced as such.
 * Every call independently runs `threatmetrix.profile()` with a FRESH session
 * id, and `window.Radar` is published asynchronously about a second after a
 * profile run. Concurrent calls therefore race on one tag: none of them
 * stabilises inside the `waitForRadar` ceiling and they all report
 * `sdk_unavailable`.
 *
 * Observed on test2, 2026-10-09 12:58-12:59. A re-check loop put roughly thirty
 * checks in flight at once and every one of the 164 came back
 * `sdk_unavailable`, against zero in the surrounding twelve hours. The loop is
 * fixed, but the loop was only what made the overlap visible: the login gate
 * and a resume re-check can legitimately overlap, and two of them are enough to
 * poison each other.
 *
 * Keyed by GUID so a logout-then-login as a different player is never handed
 * the previous player's verdict -- that one would be bound to the wrong subject
 * and refused at the server, which is safe but confusing. Cleared on settle, so
 * this coalesces concurrent callers and never caches a result.
 */
let inFlight: { playerGUID: string; promise: Promise<GeofenceOutcome> } | null = null;

/**
 * Test-only: drop the in-flight entry so each case starts clean.
 *
 * Mirrors `_resetModeCache` and friends on the server side. Without it a case
 * that leaves a check pending -- which several do deliberately, to exercise the
 * timeout paths -- wedges every later case for the same player onto a promise
 * that will never settle.
 */
export function _resetGeofenceInFlight() {
  inFlight = null;
}

export async function runLoginGeofenceCheck(playerGUID: string): Promise<GeofenceOutcome> {
  if (inFlight && inFlight.playerGUID === playerGUID) {
    console.log('[geofence] check already in flight — joining it rather than starting a second');
    return inFlight.promise;
  }

  // A DIFFERENT PLAYER WAITS RATHER THAN RACING.
  //
  // Coalescing by GUID alone left an account switch able to start a second
  // check while the first was still running, and the two would then profile
  // through the same ThreatMetrix tag and call `setUserId` on the one
  // `window.Radar` singleton. That is the same overlap this guard exists to
  // prevent -- it just arrives by a different route, and it corrupts the
  // identity the verdict is bound to rather than merely wasting a lookup.
  //
  // Chained rather than rejected so the second player still gets a real answer.
  // The previous check's outcome is discarded: it belongs to the other GUID,
  // and returning it would hand this player a token bound to someone else.
  const previous = inFlight?.promise;
  const promise = previous
    ? previous.then(
        () => runLoginGeofenceCheckUncoalesced(playerGUID),
        () => runLoginGeofenceCheckUncoalesced(playerGUID)
      )
    : runLoginGeofenceCheckUncoalesced(playerGUID);

  inFlight = { playerGUID, promise };
  try {
    return await promise;
  } finally {
    // Only clear our own entry: a later call for a different player has already
    // replaced it, and clearing that would undo their coalescing.
    if (inFlight?.promise === promise) inFlight = null;
  }
}

async function runLoginGeofenceCheckUncoalesced(playerGUID: string): Promise<GeofenceOutcome> {
  const mock = mockOutcome();
  if (mock) {
    console.log(`[geofence] using mock outcome: ${mock.status} (player ${playerGUID})`);
    return mock;
  }

  const apiKey = process.env.REACT_APP_GEOFENCE_API_KEY;
  if (!isUsableApiKey(apiKey)) {
    console.warn(
      `[geofence] REACT_APP_GEOFENCE_API_KEY is ${apiKey ? 'a placeholder' : 'not set'}`
    );
    return { status: 'unavailable', code: 'missing_api_key' };
  }

  // Radar is published by the ThreatMetrix tag, not by a script tag of ours, and
  // only once profiling has run. Kick it off and keep the session id: passing it
  // to Radar as `tmx:web_session_id` is how the vendor's own sample correlates a
  // geofence result with its device-profiling event, which is what lets anyone
  // investigating a flagged login see both halves of the same visit.
  let tmxSessionId: string | null = null;
  try {
    // requireProfiling: profiling is what publishes `window.Radar`, so the
    // geofence cannot be skipped along with risk scoring. Production runs with
    // the risk bypass ON, and without this the vendor never loads there.
    tmxSessionId = await getNewSessionId(TMXPageData.Login, playerGUID, {
      requireProfiling: true,
      // Return once profile() has been called, not once TMX reports it
      // finished. profile() is what publishes window.Radar; the completion
      // callback can take the full 30s and is the risk path's signal, not
      // ours. Waiting on it held the player at the login gate long after
      // Radar was ready.
      waitForCompletion: false
    });
  } catch (err) {
    // Profiling is best-effort here. Radar may already be present from an
    // earlier profiled event on this page, so a failure is not yet fatal.
    console.warn('[geofence] could not start ThreatMetrix profiling:', err);
  }

  const radar = await waitForRadar();
  if (!radar) {
    // Either the client library never loaded (privacy extension, missing script
    // tag) or it loaded but never published the global. Distinct code so a
    // blocked script can be told apart from a vendor that answered badly.
    console.warn(`[geofence] window.Radar never appeared within ${RADAR_LOAD_TIMEOUT_MS}ms`);
    return { status: 'unavailable', code: 'sdk_unavailable' };
  }

  return new Promise<GeofenceOutcome>((resolve) => {
    let settled = false;
    const settle = (outcome: GeofenceOutcome) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      resolve(outcome);
    };

    // A vendor that never answers must not hold the player at a spinner.
    // Deliberately `client_timeout` rather than `timeout`: Radar can report a
    // timeout of its own through onError, and conflating the two would make the
    // observe-mode data unable to distinguish "they gave up and told us" from
    // "they never answered", which need different fixes.
    const timeoutId = window.setTimeout(
      () => settle({ status: 'unavailable', code: 'client_timeout' }),
      SOFT_CHECK_TIMEOUT_MS
    );

    try {
      radar.initialize(apiKey);
      radar.setUserId(playerGUID);
      if (tmxSessionId) {
        radar.setMetadata?.({ 'tmx:web_session_id': tmxSessionId });
      }

      // Registered before the check so a synchronous failure is still caught.
      radar.onError((err) => {
        const code = err?.status || 'vendor_error';
        console.warn('[geofence] vendor error:', code);
        settle({ status: 'unavailable', code });
      });

      // A token can arrive here as well as through the promise. Whichever lands
      // first wins; `settle` makes the second a no-op.
      radar.onTokenUpdated((token) => settle(toOutcome(token)));

      // `skipVerifyApp` is what removes the desktop companion app requirement.
      // See the compliance note in docs: the vendor's own sample describes this
      // as bypassing a GLI requirement, so it is a regulatory decision, not
      // merely a UX one.
      radar
        .trackVerified({ skipVerifyApp: true })
        .then((result) => settle(toOutcome(result)))
        .catch((err: { status?: string }) => {
          const code = err?.status || 'vendor_rejected_promise';
          console.warn('[geofence] trackVerified rejected:', code);
          settle({ status: 'unavailable', code });
        });
    } catch (err) {
      console.error('[geofence] SDK threw:', err);
      settle({ status: 'unavailable', code: 'sdk_threw' });
    }
  });
}
